import {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
  ICellRendererParams,
  CellClassParams,
} from 'ag-grid-community'
import {
  isAssociatedWithTheWbsItemType,
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertyType,
} from '../../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { i18nLabelVoService } from '../../../../../../domain/value-object/I18nLabelVO'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import store from '../../../../../../store'
import { requireSave } from '../../../../../../store/requiredSaveData'
import { DefaultCellRenderer } from '../../../components/cellRenderer'
import { Option } from '../'
import { checkboxColumnDef } from './checkbox'
import { dateColumnDef } from './date'
import { datetimeColumnDef } from './datetime'
import { entitySearchColumnDef } from './entitySearch'
import { multiLineTextColumnDef } from './multLlineText'
import { numberColumnDef } from './number'
import { selectColumnDef } from './select'
import { multiSelectColumnDef } from './multiSelect'
import { textColumnDef } from './text'
import { GetEntitySearchReferenceEntityRepository } from '../../../../../hooks/useWbsItemAdditionalProperties'
import { BulkSheetColumnDef } from '../../../model'
import { ApplicationFunctionPropertyConfiguration } from '../../../../../../lib/commons/appFunction'
import { intl } from '../../../../../../i18n'
import { WbsItemAdditionalPropertyRepository } from '../../../../../../applications/ports/wbsItemAdditionalPropertyRepository'

export const wbsItemAdditionalPropertyColumnDef = <RowData>(
  projectUuid: string,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  wbsItemAdditionalPropertyRepository: WbsItemAdditionalPropertyRepository,
  option: Option<RowData>,
  getEntitySearchRepositories: GetEntitySearchReferenceEntityRepository,
  propertyConfiguration?: ApplicationFunctionPropertyConfiguration
): BulkSheetColumnDef<RowData> => {
  const columnDef: BulkSheetColumnDef<RowData> = {
    externalId: wbsItemAdditionalProperty.uuid,
    colId: wbsItemAdditionalProperty.uuid,
    field:
      wbsItemAdditionalProperty.uuid as BulkSheetColumnDef<RowData>['field'],
    headerName: i18nLabelVoService.getLabel(
      wbsItemAdditionalProperty.propertyNameI18n
    ),
    hide: false,
    editable: (params: EditableCallbackParams<RowData>) => {
      if (!params.data) return false
      let editable = false
      if (
        propertyConfiguration?.editableIfC ||
        propertyConfiguration?.editableIfU
      ) {
        editable = Boolean(
          propertyConfiguration?.editableIfC?.isTrue ||
            propertyConfiguration?.editableIfU?.isTrue
        )
        return (
          editable &&
          isAssociatedWithTheWbsItemType(
            wbsItemAdditionalProperty,
            option.getWbsItemType(params.data)
          )
        )
      }
      return isAssociatedWithTheWbsItemType(
        wbsItemAdditionalProperty,
        option.getWbsItemType(params.data)
      )
    },
    cellClassRules: {
      'ag-cell-hidden-and-disable': (params: CellClassParams<RowData>) =>
        !wbsItemAdditionalPropertyValuesVoService.hasValue(params.value) &&
        !!params.data &&
        !isAssociatedWithTheWbsItemType(
          wbsItemAdditionalProperty,
          option.getWbsItemType(params.data)
        ),
      'ag-cell-disable': (params: CellClassParams<RowData>) =>
        wbsItemAdditionalPropertyValuesVoService.hasValue(params.value) &&
        !!params.data &&
        !isAssociatedWithTheWbsItemType(
          wbsItemAdditionalProperty,
          option.getWbsItemType(params.data)
        ),
      'grid-edited-cell': (params: CellClassParams<RowData>) => {
        const { colDef, data, value } = params
        if (!data) return false
        const field = colDef.field ?? colDef.colId
        const editedAdditionalPropertyValues = (data as any)?.editedData
          ?.additionalPropertyValues
        if (
          !editedAdditionalPropertyValues ||
          !field ||
          !colDef.editable ||
          (typeof colDef.editable === 'function' && !colDef.editable(params))
        ) {
          return false
        }
        const wbsItemAdditionalPropertyValues =
          option.getAdditionalPropertyValues(data)

        if (!wbsItemAdditionalPropertyValues) {
          return false
        }
        return (
          wbsItemAdditionalPropertyValuesVoService.existIn(
            editedAdditionalPropertyValues,
            field
          ) &&
          !wbsItemAdditionalPropertyValuesVoService.equal(
            editedAdditionalPropertyValues,
            wbsItemAdditionalPropertyValues,
            wbsItemAdditionalProperty
          )
        )
      },
    },
    resizable: true,
    valueGetter: (params: ValueGetterParams<RowData>) => {
      if (!params.data) return undefined
      const additionalPropertyValuesVO = option.getAdditionalPropertyValues(
        params.data
      )
      if (!additionalPropertyValuesVO) return undefined
      return wbsItemAdditionalPropertyValuesVoService.getValue(
        additionalPropertyValuesVO,
        wbsItemAdditionalProperty.uuid
      )
    },
    valueSetter: (params: ValueSetterParams<RowData>) => {
      const { oldValue, newValue: _newValue, data, node, api, colDef } = params
      const newValue =
        wbsItemAdditionalPropertyValuesVoService.parseValueFromString(
          wbsItemAdditionalProperty.propertyType,
          _newValue
        )
      if (
        ((oldValue === null || oldValue === undefined) &&
          (newValue === null || newValue === undefined)) ||
        oldValue === newValue ||
        !data ||
        !node
      ) {
        return false
      }
      let additionalPropertyValues = option.getAdditionalPropertyValues(data)
      if (!additionalPropertyValues) {
        additionalPropertyValues =
          wbsItemAdditionalPropertyValuesVoService.construct()
      }
      option.setAdditionalPropertyValues(
        node,
        wbsItemAdditionalProperty,
        oldValue,
        newValue
      )
      store.dispatch(requireSave())
      return true
    },
    cellRenderer: DefaultCellRenderer,
    cellRendererParams: {
      isAdditionalProperty: true, // TODO Remove.
      wbsItemAdditionalProperty,
      validate: (value, params: Pick<ICellRendererParams, 'data'>) => {
        const type = option.getWbsItemType(params.data)
        // validation before fetch body (Can't get the type before fetch body)
        if (wbsItemAdditionalProperty.required && !type) {
          return intl.formatMessage({ id: 'boolExpression.requiredField' })
        }
        if (
          !isAssociatedWithTheWbsItemType(
            wbsItemAdditionalProperty,
            option.getWbsItemType(params.data)
          )
        ) {
          return
        }
        return wbsItemAdditionalPropertyValuesVoService.validate(
          wbsItemAdditionalProperty,
          value,
          propertyConfiguration
            ? {
                required: propertyConfiguration.requiredIf
                  ? propertyConfiguration.requiredIf.isTrue
                  : undefined,
              }
            : undefined
        )
      },
    },
  }

  if (propertyConfiguration) {
    if (propertyConfiguration.hiddenIfC || propertyConfiguration.hiddenIfU) {
      columnDef.hide =
        propertyConfiguration.hiddenIfC?.isTrue ||
        propertyConfiguration.hiddenIfU?.isTrue
    }
  }

  switch (wbsItemAdditionalProperty.propertyType) {
    case WbsItemAdditionalPropertyType.NUMBER:
      numberColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.TEXT:
      textColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
      multiLineTextColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.DATE:
      dateColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.DATE_TIME:
      datetimeColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.CHECKBOX:
      checkboxColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.SELECT:
      selectColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      multiSelectColumnDef(columnDef, wbsItemAdditionalProperty, option)
      break
    case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
      entitySearchColumnDef(
        columnDef,
        projectUuid,
        wbsItemAdditionalProperty,
        wbsItemAdditionalPropertyRepository,
        option,
        getEntitySearchRepositories
      )
      break
    default:
      throw new Error(
        `This wbs item additional property type(=${wbsItemAdditionalProperty.propertyType}) is not supported.`
      )
  }
  return columnDef
}
